import {
	AfterViewInit,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	linkedSignal,
	signal,
	untracked,
	ViewChild,
} from '@angular/core';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { UserClient } from '../../../api/app-types/client.app';
import { USER_SERVICE } from '../../../api/services/user.service';
import { TransferRequestItem } from '../../../api/api-types/client.api';
import { User, UserClientsQuery } from '../../../api/app-types/user.app';
import { UserClientAssignment } from '../../../api/app-types/assignment';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { PageEvent } from '@angular/material/paginator';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';
import { Paginator } from '../../../core/components/paginator/paginator';
import { LvrHeading } from '../../../core/components/heading/heading';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { LvrField } from '../../../core/components/field/field';
import { MatInputModule } from '@angular/material/input';
import { AssignmentItemToggleEvent, UserClientAssignmentItem } from './user-client-assignment-item';
import { MatDivider } from '@angular/material/divider';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { AbstractControl, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { hasError } from '../../../shared/helpers/has-error.func';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import {
	formatDateToISOString,
	matDateProviders,
	normalizeToEndOfDay,
	normalizeToStartOfDay,
} from '../../../shared/helpers/date.func';
import { maxDateValidator, minDateValidator } from '../../../shared/validators/max-date-validator';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { DatePipe } from '@angular/common';
import { RECORD_MANAGER } from '../../../core/services/record.provide';
import { AssignmentRequestType } from '../../client/request-client-dialog/request-client-dialog';

export type SearchMode = 'client' | 'owner' | 'assignmentGuid';

export interface FinishedClientGroup {
	clientId: number;
	clientFullName: string;
	finished: UserClientAssignment[];
}

export interface AssignmentMoveDateUiState {
	required: boolean;
	minDate?: string;
	maxDate?: string;
}

@Component({
	selector: 'lvr-user-assignment-transfer',
	imports: [
		Paginator,
		TranslocoPipe,
		LvrHeading,
		LvrDefaultActions,
		LvrPaddingDirective,
		LvrField,
		MatInputModule,
		UserClientAssignmentItem,
		MatDivider,
		LvrButton,
		ReactiveFormsModule,
		MatDatepickerModule,
		LvrAppendAsteriskPipe,
		LvrInputDirective,
		DatePipe,
	],
	providers: [matDateProviders],
	templateUrl: './user-assignment-transfer.html',
	styleUrl: './user-assignment-transfer.scss',
})
export class UserAssignmentTransfer extends RouteInjection implements AfterViewInit {
	@ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;
	private searchTimeoutId?: number;

	private readonly env = inject(ENVIRONMENT);
	private readonly api = inject(USER_SERVICE);
	private readonly session = inject(SessionManagerService);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly recordManager = inject(RECORD_MANAGER);

	private readonly user = this.resolveDataAsSignal<User>('user');
	private readonly targetUserId = computed(() => this.user()?.id);
	public readonly mode = signal<SearchMode>('client');
	public readonly term = signal('');
	public readonly assignmentType = signal('');
	public readonly loading = signal(false);
	public readonly saving = signal(false);

	private readonly selected = signal<Map<string, TransferRequestItem>>(new Map());
	public readonly selectedCount = computed(() => this.selected().size);

	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly dateFormatHint = this.env.visualDateFormat;

	public readonly moveDateCtrl = new FormControl<Date | null>(null);
	public readonly globalDesiredFromDate = signal<string>('');

	public readonly globalMinMoveDate = computed(() => {
		const date = normalizeToStartOfDay(new Date());
		date.setDate(date.getDate() - (this.env.formConfiguration.contact.dateWarningRange.max ?? 42));
		return date;
	});

	public readonly globalMaxMoveDate = computed(() => normalizeToEndOfDay(new Date()));

	public hasError = (control: AbstractControl | null, ...errors: string[]) => hasError(control, ...errors);

	private readonly page = this.resolveDataAsSignal<PaginatedResponse<UserClient>>('clients');
	private readonly initialPage = computed<PaginatedResponse<UserClient>>(() => {
		const pagination = this.page();
		return {
			entities: pagination?.entities ?? [],
			offset: pagination?.offset ?? 0,
			range: pagination?.range ?? this.env.pagination.range,
			totalEntities: pagination?.totalEntities ?? 0,
		};
	});

	public readonly linkedPage = linkedSignal({
		source: this.initialPage,
		computation: (init) => ({
			...init,
			range: init.range || this.env.pagination.range,
		}),
	});

	public readonly clients = computed(() => this.linkedPage().entities ?? []);
	public readonly totalItems = computed(() => this.linkedPage().totalEntities ?? 0);
	public readonly pageIndex = computed(() => this.linkedPage().offset ?? 0);
	public readonly pageSize = computed(() => this.linkedPage().range ?? this.env.pagination.range);
	private readonly canReassignAssignmentsPermission = hasPermission(this.session.permissions, 'Assignments.Reassign');
	public readonly canTransferAssignmentsPermission = computed(() => this.canReassignAssignmentsPermission());

	public readonly canSave = computed(() => {
		return (
			this.canTransferAssignmentsPermission() &&
			!this.saving() &&
			this.selected().size > 0 &&
			this.targetUserId() != null &&
			!this.hasMissingRequiredDates()
		);
	});

	private readonly initialMode = this.resolveParamAsSignal<SearchMode>('mode');
	private readonly initialAssignmentType = this.resolveParamAsSignal<AssignmentRequestType>('assignmentType');
	private readonly initialEncodedTerm = this.resolveParamAsSignal<string>('term');
	private initialSearchApplied = false;
	private initialAssignmentSelectionApplied = false;

	private readonly lastTitle = signal<string | null>(null);
	private readonly recordTitle = computed(() => {
		const user = this.user();
		if (!user) return 'Fachkraft';
		const lastName = user.lastName?.trim() ?? '';
		const firstName = user.firstName?.trim() ?? '';
		if (lastName && firstName) return `${lastName}, ${firstName}`;
		return lastName || firstName || 'Fachkraft';
	});

	public constructor() {
		super();

		// Set the tab title based on the user once it's loaded
		effect(() => {
			const title = this.recordTitle();
			if (this.lastTitle() === title) return;
			// To avoid effect loop
			untracked(() => this.recordManager.updateRecordTitleOfActiveRecord(title));
			this.lastTitle.set(title);
		});

		effect(() => {
			this.moveDateCtrl.setValidators([
				Validators.required,
				minDateValidator(this.globalMinMoveDate()),
				maxDateValidator(this.globalMaxMoveDate()),
			]);

			this.moveDateCtrl.updateValueAndValidity({ emitEvent: false });
		});

		this.moveDateCtrl.valueChanges.subscribe((value) => {
			if (this.moveDateCtrl.invalid || !value) {
				this.globalDesiredFromDate.set('');
				this.applyGlobalDateToSelected();
				return;
			}

			const formattedDate = formatDateToISOString(value);
			this.globalDesiredFromDate.set(formattedDate ?? '');
			this.applyGlobalDateToSelected();
		});

		// Keep query param values in sync with the local signals before the view is initialized.
		// The initial search itself is triggered in ngAfterViewInit, once the search input is available.
		effect(() => {
			const mode = this.initialMode();
			const encodedTerm = this.initialEncodedTerm();
			const term = this.decodeSearchTerm(encodedTerm);
			const type = this.initialAssignmentType() ?? '';

			if (mode !== 'client' && mode !== 'owner' && mode !== 'assignmentGuid') return;
			if (!term) return;

			this.mode.set(mode);
			this.term.set(term);
			this.assignmentType.set(type);
		});
	}

	public ngAfterViewInit() {
		if (this.initialSearchApplied) return;

		const mode = this.initialMode();
		const term = this.term().trim();

		if (mode !== 'client' && mode !== 'owner' && mode !== 'assignmentGuid') return;
		if (!term) return;

		this.initialSearchApplied = true;

		if (this.searchInput?.nativeElement) {
			this.searchInput.nativeElement.value = term;
		}

		void this.search();
	}

	public setMode(m: SearchMode) {
		if (this.mode() === m) return;

		this.mode.set(m);
		this.term.set('');
		this.selected.set(new Map());
		this.moveDateCtrl.setValue(null, { emitEvent: false });
		this.globalDesiredFromDate.set('');

		const input = this.searchInput?.nativeElement;
		if (input) input.value = '';

		void this.reloadPage(0, this.pageSize(), true);
	}

	private buildQuery(pageIndex: number, pageSize: number): UserClientsQuery {
		const t = this.term().trim();
		const base: UserClientsQuery = { offset: pageIndex, range: pageSize, userIdToExclude: this.user()?.id };

		if (!t) return base;

		switch (this.mode()) {
			case 'client':
				return { ...base, clientNameFilter: t };
			case 'owner':
				return { ...base, ownerNameFilter: t };
			case 'assignmentGuid':
				return { ...base, assignmentGuidFilter: t };
		}
	}

	public async search() {
		await this.reloadPage(0, this.pageSize(), true);
	}

	public handlePageEvent(event: PageEvent) {
		this.reloadPage(event.pageIndex, event.pageSize);
	}

	private async reloadPage(pageIndex: number, pageSize: number, clearSelection = false) {
		this.loading.set(true);
		try {
			const q = this.buildQuery(pageIndex, pageSize);
			const update = await this.api.getUserClients(q);

			this.linkedPage.set({
				...update,
				offset: pageIndex,
				range: pageSize,
			});

			if (clearSelection) {
				this.selected.set(new Map());
			}

			// Applies the initial assignment selection based on the URL parameter
			this.applyInitialAssignmentSelection();
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(
					this.transloco.translate('base.notification_message.user_client_assignments_reloading_error'),
				),
			);
		} finally {
			this.loading.set(false);
		}
	}

	public onItemToggled(event: AssignmentItemToggleEvent) {
		if (event.kind === 'fds') {
			this.toggleCreateFds(event.clientId, event.checked);
			return;
		}

		this.toggle(event.clientId, event.assignment, event.checked);
	}

	public readonly hasMissingRequiredDates = computed(() => {
		return Array.from(this.selected().values()).some((item) => {
			return !item.desiredFromDate;
		});
	});

	private toggle(clientId: number, a: UserClientAssignment, checked: boolean) {
		const k = this.key(clientId, a.id);
		const next = new Map(this.selected());

		if (!checked) {
			next.delete(k);
			this.selected.set(next);
			return;
		}

		const item: TransferRequestItem = {
			clientId,
			assignmentId: a.id,
			operation: a.validOperation,
			desiredFromDate: this.globalDesiredFromDate(),
		};

		next.set(k, item);
		this.selected.set(next);
	}
	private toggleCreateFds(clientId: number, checked: boolean) {
		const k = this.fdsKey(clientId);
		const next = new Map(this.selected());

		if (!checked) {
			next.delete(k);
			this.selected.set(next);
			return;
		}

		next.set(k, {
			clientId,
			operation: 'create-sos',
			desiredFromDate: this.globalDesiredFromDate(),
		});

		this.selected.set(next);
	}

	public selectAllOnPage() {
		const next = new Map(this.selected());

		for (const c of this.clients()) {
			for (const a of c.assignments ?? []) {
				if (!a.validOperation || !this.isSelectableActivePhase(a)) continue;

				const k = this.key(c.id, a.id);

				next.set(k, {
					clientId: c.id,
					assignmentId: a.id,
					operation: a.validOperation,
					desiredFromDate: a.validOperation === 'create-sos' ? undefined : this.globalDesiredFromDate() || undefined,
				});
			}
		}

		this.selected.set(next);
	}

	public clearSelection() {
		this.selected.set(new Map());
	}

	public async save() {
		if (this.saving() || !this.canSave()) return;

		const target = this.targetUserId();
		if (!target) return;

		const body = { request: Array.from(this.selected().values()) };

		this.saving.set(true);
		try {
			await this.api.transferAssignments(target, body);
			await this.cancel();
			this.ntfy.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.user_client_assignments_moved')),
			);
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(
					this.transloco.translate('base.notification_message.user_client_assignments_move_error'),
				),
			);
		} finally {
			this.saving.set(false);
		}
	}

	public async cancel() {
		await this.router.navigate(['../../'], { relativeTo: this.route });
	}

	public updateTerm(event: Event) {
		clearTimeout(this.searchTimeoutId);

		const value = event.target ? (event.target as HTMLInputElement).value : '';

		this.searchTimeoutId = window.setTimeout(() => {
			this.term.set(value);

			const trimmed = this.term().trim();

			// Reload without filters when empty
			if (trimmed.length === 0) {
				this.search();
				return;
			}

			// Look when there are at least 2 characters
			if (trimmed.length >= 2) {
				this.search();
			}
		}, 300);
	}

	public readonly searchPlaceholderKey = computed(() => {
		switch (this.mode()) {
			case 'client':
				return 'user.assignment_transfer.client_placeholder';
			case 'owner':
				return 'user.assignment_transfer.user_placeholder';
			case 'assignmentGuid':
				return 'user.assignment_transfer.assignment_number_placeholder';
		}
	});

	public readonly finishedSectionClients = computed<FinishedClientGroup[]>(() => {
		return this.clients()
			.map((c) => {
				const finished = this.sortAssignments((c.assignments ?? []).filter((a) => this.isFinished(a)));
				return { clientId: c.id, clientFullName: c.clientFullName, finished };
			})
			.filter((g) => g.finished.length > 0);
	});

	public readonly activeSectionClients = computed(() => {
		return this.clients().map((c) => ({
			clientId: c.id,
			clientFullName: c.clientFullName,
			active: this.sortAssignments((c.assignments ?? []).filter((a) => !this.isFinished(a))),
		}));
	});

	public isSelected(clientId: number, assignmentId: number) {
		return this.selected().has(this.key(clientId, assignmentId));
	}

	public isFdsSelected(clientId: number) {
		return this.selected().has(this.fdsKey(clientId));
	}

	private key(clientId: number, assignmentId: number) {
		return `c:${clientId}:a:${assignmentId}`;
	}

	private fdsKey(clientId: number) {
		return `fds:c:${clientId}`;
	}

	private isSelectableActivePhase(a: UserClientAssignment) {
		return a.phase === 'P1' || a.phase === 'P2' || a.phase === 'P3' || a.phase === 'FP3';
	}

	private isFinished(a: UserClientAssignment) {
		return a.phase === 'P4' || a.phase === 'FP4' || a.phase === 'FP5';
	}

	public isReOwnershipP0(a: UserClientAssignment) {
		return a.phase === 'P0' && !a.currentPhaseStart && a.validOperation === 're-ownership';
	}

	private readonly assignmentTypeOrder: Record<string, number> = {
		AT: 1,
		ATZ: 2,
		FDS: 3,
	};

	private sortAssignments(assignments: UserClientAssignment[]) {
		return [...assignments].sort((a, b) => {
			const orderA = this.assignmentTypeOrder[a.assignmentType] ?? 99;
			const orderB = this.assignmentTypeOrder[b.assignmentType] ?? 99;
			return orderA - orderB;
		});
	}

	private applyGlobalDateToSelected() {
		const globalDate = this.globalDesiredFromDate();
		const next = new Map(this.selected());

		for (const [key, item] of next.entries()) {
			next.set(key, {
				...item,
				desiredFromDate: globalDate,
			});
		}

		this.selected.set(next);
	}

	private decodeSearchTerm(encodedTerm: string | null | undefined): string {
		if (!encodedTerm) return '';

		try {
			return decodeURIComponent(atob(encodedTerm));
		} catch {
			return '';
		}
	}

	private applyInitialAssignmentSelection() {
		if (this.initialAssignmentSelectionApplied) return;

		const assignmentType = this.assignmentType();

		if (assignmentType !== AssignmentRequestType.Main && assignmentType !== AssignmentRequestType.Fds) return;

		const clients = this.clients();
		if (clients.length === 0) return;

		if (assignmentType === AssignmentRequestType.Main) {
			const result = this.findInitialMainAssignment(clients);
			if (!result) return;

			this.toggle(result.clientId, result.assignment, true);
			this.initialAssignmentSelectionApplied = true;
			return;
		}

		const client = clients[0];

		this.toggleCreateFds(client.id, true);
		this.initialAssignmentSelectionApplied = true;
	}

	private findInitialMainAssignment(clients: UserClient[]) {
		for (const client of clients) {
			const assignment = (client.assignments ?? []).find((a) => {
				return a.assignmentType === 'AT' && a.phase !== 'P4' && !!a.validOperation;
			});

			if (assignment) {
				return {
					clientId: client.id,
					assignment,
				};
			}
		}

		return undefined;
	}
}
